/**
 * Vocabulário da app partilhado pelas Functions, pelo backoffice (`src/`) e pelo
 * portal: etapas, estados, definições por omissão e as formas que a API REST
 * devolve. Sem dependências — corre no browser e no Worker.
 */

// ─── Reclamações ──────────────────────────────────────────────────────────────

/** Etapas do tratamento (ISO 9001 §8.7/§10.2), pela ordem da ficha. */
export const STAGES = ["registo", "analise", "causa", "acao", "resposta", "fecho", "eficacia"] as const
export type Stage = (typeof STAGES)[number]

export const STAGE_LABELS: Record<Stage, string> = {
  registo: "Registo",
  analise: "Análise",
  causa: "Causa",
  acao: "Ação corretiva",
  resposta: "Resposta",
  fecho: "Fecho",
  eficacia: "Eficácia",
}

/**
 * Configuração de uma etapa (Administração → Etapas). `label` é o nome no
 * backoffice; `public_label` o que o cliente lê no acompanhamento; `enabled`
 * tira a etapa do circuito (Registo e Fecho não se desligam); `required` exige
 * o texto da etapa antes de se avançar (ver `advanceBlocker`).
 */
export interface StageConfig {
  label: string
  public_label: string
  enabled: boolean
  required: boolean
}

/** Etapas que não se desligam: sem elas não há reclamação nem fecho. */
export const LOCKED_STAGES: readonly Stage[] = ["registo", "fecho"]

export const DEFAULT_STAGE_CONFIG: Record<Stage, StageConfig> = {
  registo: { label: "Registo", public_label: "Reclamação recebida", enabled: true, required: false },
  analise: { label: "Análise", public_label: "Em análise", enabled: true, required: true },
  causa: { label: "Causa", public_label: "Causa identificada", enabled: true, required: true },
  acao: { label: "Ação corretiva", public_label: "Em correção", enabled: true, required: true },
  resposta: { label: "Resposta", public_label: "Resposta enviada", enabled: true, required: true },
  fecho: { label: "Fecho", public_label: "Reclamação resolvida", enabled: true, required: true },
  eficacia: { label: "Eficácia", public_label: "Reclamação resolvida", enabled: true, required: false },
}

/** As etapas ativas, pela ordem da ficha. */
export function enabledStages(stages: Record<Stage, StageConfig>): Stage[] {
  return STAGES.filter((s) => LOCKED_STAGES.includes(s) || stages[s]?.enabled !== false)
}

/** A próxima etapa ativa depois de `from` (ou `undefined` no fim). */
export function nextStage(stages: Record<Stage, StageConfig>, from: Stage): Stage | undefined {
  const active = enabledStages(stages)
  return active.find((s) => STAGES.indexOf(s) > STAGES.indexOf(from))
}

export const stageLabel = (stages: Record<Stage, StageConfig>, s: Stage) => stages[s]?.label || STAGE_LABELS[s]

/**
 * Porque é que a reclamação não pode sair da etapa atual (texto para o
 * utilizador), ou `null` quando pode. A regra é a mesma no servidor e no
 * backoffice: cada etapa obrigatória pede o seu texto — Ação corretiva aceita
 * a correção imediata **ou** uma ação registada.
 */
export function advanceBlocker(
  c: Pick<Complaint, "stage" | "metadata">,
  actionsCount: number,
  stages: Record<Stage, StageConfig>,
): string | null {
  const cfg = stages[c.stage]
  if (!cfg?.required) return null
  const filled = (f: NoteField) => typeof c.metadata?.[f] === "string" && String(c.metadata[f]).trim() !== ""
  const label = stageLabel(stages, c.stage)
  switch (c.stage) {
    case "analise":
      return filled("analysis") ? null : `Preencha a análise antes de sair de «${label}».`
    case "causa":
      return filled("root_cause") ? null : `Descreva a causa antes de sair de «${label}».`
    case "acao":
      return filled("immediate_correction") || actionsCount > 0
        ? null
        : `Registe a correção imediata ou uma ação antes de sair de «${label}».`
    case "resposta":
      return filled("response_text") ? null : `Escreva a resposta ao cliente antes de sair de «${label}».`
    default:
      return null
  }
}

export const COMPLAINT_STATUSES = ["open", "closed", "cancelled"] as const
export type ComplaintStatus = (typeof COMPLAINT_STATUSES)[number]

export const STATUS_LABELS: Record<ComplaintStatus, string> = {
  open: "Em tratamento",
  closed: "Encerrada",
  cancelled: "Anulada",
}

export const CHANNELS = ["portal", "email", "interno"] as const
export type Channel = (typeof CHANNELS)[number]

export const CHANNEL_LABELS: Record<Channel, string> = {
  portal: "Portal do cliente",
  email: "Email",
  interno: "Registo interno",
}

export type IdentityStatus = "verified" | "unverified" | "anonymous"

export const IDENTITY_LABELS: Record<IdentityStatus, string> = {
  verified: "Cliente verificado",
  unverified: "Por verificar",
  anonymous: "Sem cliente",
}

/**
 * Campos de texto da ficha que vivem em `metadata` (uma coluna por etapa não
 * acrescenta nada à pesquisa e obrigaria a mexer na DDL por cada texto novo).
 */
export const NOTE_FIELDS = ["analysis", "root_cause", "immediate_correction", "response_text", "closing_notes"] as const
export type NoteField = (typeof NOTE_FIELDS)[number]

export const NOTE_LABELS: Record<NoteField, string> = {
  analysis: "Análise",
  root_cause: "Causa",
  immediate_correction: "Correção imediata",
  response_text: "Resposta ao cliente",
  closing_notes: "Notas de fecho",
}

export const ACTION_KINDS = ["immediate", "corrective", "preventive"] as const
export type ActionKind = (typeof ACTION_KINDS)[number]

export const ACTION_KIND_LABELS: Record<ActionKind, string> = {
  immediate: "Correção imediata",
  corrective: "Ação corretiva",
  preventive: "Ação preventiva",
}

export type ActionStatus = "open" | "done" | "cancelled"
export type Effectiveness = "effective" | "not_effective"

export interface Complaint {
  id: string
  ref: string
  channel: Channel
  entity_id: string | null
  contact_id: string | null
  ext_client_id: string | null
  ext_document_id: string | null
  identity_status: IdentityStatus
  estab: string | null
  submitted_name: string | null
  submitted_email: string | null
  submitted_phone: string | null
  category: string | null
  severity: string | null
  description: string
  expectation: string | null
  /** Tipo de resolução dado no fecho (lista `settings.resolutions`). */
  resolution_type: string | null
  /** Texto da resolução, mostrado ao cliente no acompanhamento. */
  resolution_text: string | null
  status: ComplaintStatus
  stage: Stage
  owner_user_id: string | null
  due_at: string | null
  invoice_ref: string | null
  invoice_date: string | null
  consent_at: string | null
  closed_at: string | null
  metadata: Partial<Record<NoteField, string>> & Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface ComplaintAction {
  id: string
  complaint_id: string
  kind: ActionKind
  description: string
  owner_user_id: string | null
  due_at: string | null
  status: ActionStatus
  done_at: string | null
  effectiveness_checked_at: string | null
  effectiveness_result: Effectiveness | null
  effectiveness_notes: string | null
  created_at: string
  updated_at: string
}

export interface ComplaintLogEntry {
  id: string
  complaint_id: string
  at: string
  actor_kind: "user" | "customer" | "system"
  actor_user_id: string | null
  actor_label: string | null
  action: string
  stage: Stage | null
  visibility: "internal" | "public"
  message: string | null
  changes: Record<string, { from: unknown; to: unknown }> | null
}

export interface ComplaintFile {
  id: string
  complaint_id: string
  file_key: string
  file_name: string | null
  content_type: string | null
  size_bytes: number | null
  uploaded_by_kind: "user" | "customer"
  uploaded_by_user_id: string | null
  visibility: "internal" | "public"
  created_at: string
}

export interface ComplaintLine {
  id: string
  complaint_id: string
  sku: string | null
  description: string | null
  qty_invoiced: number | null
  qty_claimed: number | null
  unit: string | null
  source: "manual" | "phc" | "erp"
}

export interface ComplaintDetail {
  complaint: Complaint
  actions: ComplaintAction[]
  log: ComplaintLogEntry[]
  files: ComplaintFile[]
  lines: ComplaintLine[]
}

/** Uma reclamação aberta passou o prazo. */
export function isOverdue(c: Pick<Complaint, "status" | "due_at">, now = Date.now()): boolean {
  return c.status === "open" && !!c.due_at && new Date(c.due_at).getTime() < now
}

// ─── Definições ───────────────────────────────────────────────────────────────

export interface SeverityDef {
  key: string
  label: string
  /** Prazo de tratamento em dias (define `due_at` ao registar). */
  sla_days: number
}

/** De onde vêm os clientes da app (inquéritos, lista de clientes). */
export type ClientsSource = "excel" | "erp" | "crm"

export const CLIENTS_SOURCE_LABELS: Record<ClientsSource, string> = {
  excel: "Só o Excel importado na app",
  erp: "Clientes do ERP ligado ao Koortex",
  crm: "Entidades do CRM",
}

export interface Settings {
  categories: string[]
  /**
   * Tipos de resolução: o que o cliente pede no portal («O que espera da nossa
   * parte?») e o que se dá no fecho — a mesma lista, para se comparar.
   */
  resolutions: string[]
  /** Etapas: nomes (interno e para o cliente), ativas e obrigatórias. */
  stages: Record<Stage, StageConfig>
  clients: {
    source: ClientsSource
    /** Com `source = "erp"`: só este ERP (`phc`, `odoo`…); vazio = todos. */
    erp_provider: string
  }
  severities: SeverityDef[]
  /** Remetente dos emails da app (o SMTP é o do tenant). */
  sender: { name: string; reply_to: string }
  portal: { intro_text: string; thanks_text: string }
  rgpd: { consent_text: string; retention_months: number }
  surveys: {
    low_score_nps: number
    low_score_avg: number
    /** A6 — um inquérito avulso por email a cada N dias (encomendas). */
    min_days_between: number
    /** A6 — emails que nunca recebem inquéritos (lista de exclusão). */
    excluded_emails: string[]
  }
  /** Objetivos que o painel compara (tempo de fecho, satisfação). */
  goals: { close_days: number; satisfaction_avg: number }
}

/**
 * Pontos de partida. O cliente define os valores reais em Administração; os
 * textos do portal e do RGPD vêm vazios de propósito (não se inventa texto legal).
 */
export const DEFAULT_SETTINGS: Settings = {
  categories: ["Qualidade do produto", "Prazo de entrega", "Encomenda errada", "Faturação", "Atendimento"],
  resolutions: ["Substituição do produto", "Reparação", "Nota de crédito", "Esclarecimento", "Apenas registar a ocorrência"],
  stages: DEFAULT_STAGE_CONFIG,
  clients: { source: "excel", erp_provider: "" },
  severities: [
    { key: "baixa", label: "Baixa", sla_days: 15 },
    { key: "media", label: "Média", sla_days: 10 },
    { key: "alta", label: "Alta", sla_days: 5 },
  ],
  sender: { name: "", reply_to: "" },
  portal: { intro_text: "", thanks_text: "" },
  rgpd: { consent_text: "", retention_months: 60 },
  surveys: { low_score_nps: 6, low_score_avg: 2, min_days_between: 30, excluded_emails: [] },
  goals: { close_days: 10, satisfaction_avg: 4 },
}

// ─── Inquéritos ───────────────────────────────────────────────────────────────

export type CampaignStatus = "draft" | "active" | "paused" | "closed"

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  draft: "Rascunho",
  active: "A decorrer",
  paused: "Em pausa",
  closed: "Terminada",
}

export interface CampaignSummary {
  id: string
  name: string
  kind: "periodic" | "event"
  status: CampaignStatus
  starts_at: string | null
  ends_at: string | null
  reminder_after_days: number | null
  reminder_max: number
  search: string | null
  sends: { total: number; sent: number; opened: number; responded: number; failed: number }
  response_rate: number | null
  nps: number | null
}

/** De onde veio o envio de um inquérito (A6). */
export type SurveyOrigin = "campaign" | "complaint" | "order"

export const SURVEY_ORIGIN_LABELS: Record<SurveyOrigin, string> = {
  campaign: "Campanha",
  complaint: "Reclamação",
  order: "Encomenda",
}

export interface SurveyResponseRow {
  id: string
  campaign_id: string
  campaign_name: string | null
  origin: SurveyOrigin
  /** Referência da origem: n.º da encomenda ou ref. da reclamação. */
  origin_ref: string | null
  entity_id: string | null
  client_label: string | null
  nps: number | null
  score_avg: number | null
  comment: string | null
  contact_requested: boolean
  submitted_at: string
}

export interface SurveysOverview {
  campaigns: CampaignSummary[]
  responses: SurveyResponseRow[]
  /** NPS e média por mês (`YYYY-MM`), dos últimos 12 meses. */
  by_month: Array<{ month: string; responses: number; nps: number | null; score_avg: number | null }>
}

/** NPS = % promotores (9–10) − % detratores (0–6), arredondado. */
export function computeNps(scores: number[]): number | null {
  if (scores.length === 0) return null
  const promoters = scores.filter((s) => s >= 9).length
  const detractors = scores.filter((s) => s <= 6).length
  return Math.round(((promoters - detractors) / scores.length) * 100)
}

// ─── Painel ───────────────────────────────────────────────────────────────────

export interface Dashboard {
  period: { from: string; to: string }
  kpis: {
    open: number
    overdue: number
    avg_close_days: number | null
    nps: number | null
    promoters_pct: number | null
    detractors_pct: number | null
    satisfaction_avg: number | null
    responses: number
    response_rate: number | null
  }
  goals: Settings["goals"]
  by_category: Array<{ category: string; count: number }>
  /** Pedido vs. dado: por tipo de resolução, quantos pediram e quantos receberam. */
  resolutions: {
    rows: Array<{ resolution: string; expected: number; given: number }>
    /** Encerradas com pedido: % em que se deu o que o cliente pediu. */
    match_pct: number | null
    closed_with_expectation: number
  }
  by_month: Array<{ month: string; received: number; closed: number; score_avg: number | null }>
  attention: Array<{
    kind: "overdue" | "due_soon" | "low_score"
    complaint_id?: string
    ref?: string
    title: string
    detail: string
  }>
  recent_complaints: Complaint[]
  recent_responses: SurveyResponseRow[]
}

// ─── Clientes (importação) ────────────────────────────────────────────────────

/** Uma linha do Excel de clientes (formato do plano §3), já lida no browser. */
export interface ClientImportRow {
  /** Linha no ficheiro (1 = cabeçalho), para os erros apontarem para o Excel. */
  row: number
  number: string
  estab: string
  name: string
  email: string
  tax_id?: string
  phone?: string
  locality?: string
  surveys_opt_in?: boolean
}

export interface ClientImportReport {
  dry_run: boolean
  received: number
  new: number
  existing: number
  /** Erros por linha do Excel (validação local + recusas do host). */
  errors: Array<{ row: number; external_id: string | null; message: string }>
  warnings: Array<{ row: number; external_id: string | null; message: string }>
  entities: { created: number; linked: number; updated: number } | null
}

/** `external_id` do cliente ERP: `"<n.º>:<estab>"` (PHC `cl.no:cl.estab`). */
export function clientExternalId(number: string, estab: string): string {
  return `${number.trim()}:${(estab.trim() || "0")}`
}
